import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import {
  InvitationMailConfirmModal,
  type InvitationSendResult,
} from '@/features/invitations/InvitationMailConfirmModal';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ProfileFields, toDraft, toInput, type ProfileDraft } from '@/features/auth/ProfileFields';
import { createInvitation } from '@/features/invitations/api';
import { STAFF_ROLE_META, type StaffRole } from '@/features/staff/StaffPage';
import { ApiError } from '@/lib/api/http';

const EMPTY_DRAFT: ProfileDraft = toDraft({
  display_name: null,
  company: null,
  position: null,
  phone: null,
  contact_email: null,
  bio: null,
});

/**
 * 理事・講師／サクセス担当それぞれの招待（事務局のみ / D-140・D-142・D-143）。
 * 受講生（/members/new）・事務局（/office-accounts/new）と同じく、
 * それぞれの一覧画面から専用の招待画面へ入る（招待の入口を1系統に統一する）。
 * 区分は画面ごとに固定（理事・講師の一覧からは理事・講師しか招待できない）。
 * 会員登録と同じく、招待の時点で分かっているプロフィールを入れておける。
 * 空のままにした項目は、本人が受諾時または「アカウント設定」から入力する。
 */
export function StaffInvitePage({ role }: { role: StaffRole }): ReactElement {
  const navigate = useNavigate();
  const meta = STAFF_ROLE_META[role];
  const [email, setEmail] = useState('');
  const [draft, setDraft] = useState<ProfileDraft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // 送る前に内容を確認してもらう（D-153）
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sendResult, setSendResult] = useState<InvitationSendResult | null>(null);

  async function onSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const created = await createInvitation({ email, role, ...toInput(draft) });
      // 受諾リンクはこの応答にしか無いので、閉じる前に結果として見せる（D-153）
      setSendResult({ acceptUrl: created.accept_url, mailSent: created.mail_sent });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '招待に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHeader title={`${meta.label}の招待`} />
      <MetaBox title="招待する相手">
        {error && (
          <Notice kind="error" dismissible={false}>
            {error}
          </Notice>
        )}

        <p className="mb-3 text-wptext-muted">
          メールアドレス（必須）のほか、分かっている項目はここで入れておけます。
          空のままにした項目は、本人が受諾後に「アカウント設定」から入力します。
        </p>

        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">メールアドレス</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-64 rounded-sm border border-wpborder px-3 py-2"
          />
        </label>

        <ProfileFields draft={draft} onChange={setDraft} idPrefix="staff-invite" />

        <div className="flex gap-2">
          <button
            type="button"
            disabled={submitting || !email}
            onClick={() => setConfirmOpen(true)}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {submitting ? '招待中…' : '招待する'}
          </button>
          <button
            type="button"
            onClick={() => navigate(meta.path)}
            className="btn-secondary"
          >
            キャンセル
          </button>
        </div>
      </MetaBox>

      <InvitationMailConfirmModal
        open={confirmOpen}
        email={email}
        busy={submitting}
        result={sendResult}
        onSend={() => void onSubmit()}
        onCancel={() => setConfirmOpen(false)}
        onDone={() => {
          setConfirmOpen(false);
          navigate(meta.path);
        }}
      />
    </>
  );
}
